import { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2, Building2, MessageCircle, HelpCircle, Languages, Mic, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ALL_LANGS = ["English", "Spanish", "French", "German", "Polish", "Italian", "Portuguese", "Arabic", "Mandarin", "Hindi", "Urdu", "Turkish"];
const VOICES = ["Emma (Female, British)", "James (Male, British)", "Olivia (Female, US)", "Noah (Male, US)", "Sofia (Female, Neutral)"];

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><Icon className="h-4.5 w-4.5" /></div>
        <div>
          <h2 className="font-heading font-bold tracking-tight">{title}</h2>
          <p className="text-xs text-slate-500">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Settings() {
  const [cfg, setCfg] = useState(null);
  const [servicesText, setServicesText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business").then((r) => {
      setCfg(r.data);
      setServicesText((r.data.services || []).join(", "));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!cfg) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading settings…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const toggleLang = (l) => set("languages", cfg.languages.includes(l) ? cfg.languages.filter((x) => x !== l) : [...cfg.languages, l]);
  const setFaq = (i, k, v) => { const f = [...cfg.faqs]; f[i] = { ...f[i], [k]: v }; set("faqs", f); };
  const addFaq = () => set("faqs", [...cfg.faqs, { question: "", answer: "", _k: `faq${Date.now()}` }]);
  const removeFaq = (i) => set("faqs", cfg.faqs.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, services: servicesText.split(",").map((s) => s.trim()).filter(Boolean) };
      const r = await api.put("/business", payload);
      setCfg(r.data);
      setServicesText((r.data.services || []).join(", "));
      toast.success("Settings saved — your AI receptionist is updated");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">AI Settings</h1>
          <p className="text-slate-500 mt-1">Configure how your AI receptionist talks and what it knows.</p>
        </div>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </Button>
      </div>

      <div className="space-y-5">
        <Section icon={Building2} title="Business profile" desc="The details your receptionist shares with callers.">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["name", "Business name"],
              ["industry", "Industry"],
              ["phone", "Phone number"],
              ["forwarding_number", "Call forwarding number"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input data-testid={`cfg-${k}`} value={cfg[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs">Opening hours</Label>
              <Input data-testid="cfg-hours" value={cfg.hours || ""} onChange={(e) => set("hours", e.target.value)} className="mt-1" />
            </div>
          </div>
        </Section>

        {cfg.vapi_assistant_id && (
          <div data-testid="receptionist-live" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white shrink-0"><CheckCircle2 className="h-5 w-5" /></div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-800">Your AI Receptionist is live</p>
              <p className="text-xs text-emerald-700/80">It's answering calls now. Save changes here to update it instantly — no need to touch anything technical.</p>
            </div>
          </div>
        )}

        <Section icon={MessageCircle} title="Greeting" desc="The first thing callers hear.">
          <Textarea data-testid="cfg-greeting" value={cfg.greeting || ""} onChange={(e) => set("greeting", e.target.value)} rows={2} />
          <div className="mt-4">
            <Label className="text-xs">Services (comma separated)</Label>
            <Input data-testid="cfg-services" value={servicesText} onChange={(e) => setServicesText(e.target.value)} className="mt-1" placeholder="Haircut, Colour, Blow-dry" />
          </div>
        </Section>

        <Section icon={HelpCircle} title="Frequently asked questions" desc="The receptionist answers these automatically.">
          <div className="space-y-3">
            {cfg.faqs.map((f, i) => (
              <div key={f._k || `faq-${i}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
                <Input data-testid={`faq-q-${i}`} value={f.question} onChange={(e) => setFaq(i, "question", e.target.value)} placeholder="Question" className="font-medium" />
                <div className="flex gap-2">
                  <Input data-testid={`faq-a-${i}`} value={f.answer} onChange={(e) => setFaq(i, "answer", e.target.value)} placeholder="Answer" />
                  <Button variant="ghost" size="icon" onClick={() => removeFaq(i)} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="add-faq-btn" variant="outline" onClick={addFaq} className="mt-3 gap-2"><Plus className="h-4 w-4" /> Add FAQ</Button>
          <div className="mt-5">
            <Label className="text-xs">Custom knowledge &amp; special instructions</Label>
            <Textarea data-testid="cfg-custom-knowledge" value={cfg.custom_knowledge || ""} onChange={(e) => set("custom_knowledge", e.target.value)} rows={4} className="mt-1" placeholder="Anything else your AI should know — policies, special offers, parking info, how to handle specific requests…" />
          </div>
        </Section>

        <Section icon={Languages} title="Languages" desc="Enable the languages your receptionist can speak.">
          <div className="flex flex-wrap gap-2">
            {ALL_LANGS.map((l) => {
              const on = cfg.languages.includes(l);
              return (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => toggleLang(l)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </Section>

        <Section icon={Mic} title="Voice" desc="The voice used for phone calls.">
          <Select value={cfg.voice} onValueChange={(v) => set("voice", v)}>
            <SelectTrigger data-testid="cfg-voice" className="max-w-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Section>
      </div>
    </div>
  );
}
